/* eslint-env browser */
import { useCallback } from "react";
import { useLocation } from "zudoku/router";
import { useJumpTo, useUrlFollowsScroll } from "./urlSync";

/*
  The address bar follows the block in view on the reference pages. They use
  the URL form their sidebar links to, so a shared or reloaded link opens at
  the block.

  Mounted once in the `content-after` slot, which Zudoku renders inside <main>
  on every page. Each page family says which pages it covers and which headings
  the hash names; away from them the component stays idle.
*/

export type PageFamily = {
  covers: (pathname: string) => boolean;
  /** Selector of the headings, which carry the hash as their `id`. */
  headings: string;
};

/* The group pages of a reference; every method is a <Method> heading. */
export const referencePages = (
  references: readonly { path: string }[],
): PageFamily => ({
  // The base path itself is the overview page and has no methods.
  covers: (pathname) =>
    references.some(({ path }) => pathname.startsWith(`${path}/`)),
  headings: ".js-ref-signature[id]",
});

/* The overview page of a reference; every topic is a markdown <h2>. */
export const referenceOverviews = (
  references: readonly { path: string }[],
): PageFamily => ({
  covers: (pathname) => references.some(({ path }) => pathname === path),
  headings: "main h2[id]",
});

const HashFollowsScroll = ({
  pathname,
  hash,
  selector,
}: {
  pathname: string;
  hash: string;
  selector: string;
}) => {
  const headings = useCallback(
    () => Array.from(document.querySelectorAll<HTMLElement>(selector)),
    [selector],
  );
  const toUrl = useCallback(
    (id?: string) => (id ? `${pathname}#${id}` : pathname),
    [pathname],
  );
  // The jump loop keeps scrolling, so hold the URL while it runs.
  const settling = useJumpTo(hash || undefined);
  const paused = useCallback(() => settling.current, [settling]);
  useUrlFollowsScroll({ headings, toUrl, paused });
  return null;
};

export const HashUrlSync = ({
  families,
}: {
  families: readonly PageFamily[];
}) => {
  const { pathname, hash } = useLocation();
  const family = families.find((candidate) => candidate.covers(pathname));

  // The key restarts the jump loop on every page of the family, not only the
  // first one the reader opens.
  return family ? (
    <HashFollowsScroll
      key={pathname}
      pathname={pathname}
      hash={decodeURIComponent(hash.slice(1))}
      selector={family.headings}
    />
  ) : null;
};
